import type { Leistung } from '../types/leistung';
import { STAMMDATEN_LIST_PAGE_SIZE } from './listPageSize';

export type LeistungenListResult = {
  rows: Leistung[];
  total: number;
  page: number;
  totalPages: number;
  searchMode: 'semantic' | 'keyword' | null;
  globalTotal?: number;
};

export async function loadLeistungenPage(
  folderId: string | null,
  query: string,
  page: number,
  signal?: AbortSignal,
): Promise<LeistungenListResult> {
  const trimmedQuery = query.trim();
  if (trimmedQuery) {
    const params = new URLSearchParams({
      q: trimmedQuery,
      page: String(Math.max(1, page)),
      mode: 'keyword',
    });
    const res = await fetch(`/api/leistungen/search?${params.toString()}`, { signal });
    const data = res.ok ? await res.json().catch(() => ({})) : {};
    const payload = data as {
      items?: Leistung[];
      total?: number;
      total_pages?: number;
      page?: number;
      mode?: 'semantic' | 'keyword';
    };
    const rows = Array.isArray(payload.items) ? payload.items : [];
    const total = Number(payload.total);
    const safeTotal = Number.isFinite(total) && total >= 0 ? total : rows.length;
    const totalPagesRaw = Number(payload.total_pages);
    const totalPages =
      Number.isFinite(totalPagesRaw) && totalPagesRaw >= 1
        ? Math.trunc(totalPagesRaw)
        : Math.max(1, Math.ceil(safeTotal / STAMMDATEN_LIST_PAGE_SIZE) || 1);
    const pageRaw = Number(payload.page);
    return {
      rows,
      total: safeTotal,
      page: Number.isFinite(pageRaw) && pageRaw >= 1 ? Math.trunc(pageRaw) : Math.max(1, page),
      totalPages,
      searchMode: payload.mode ?? 'keyword',
    };
  }

  const params = new URLSearchParams();
  params.set('with_meta', '1');
  params.set('limit', String(STAMMDATEN_LIST_PAGE_SIZE));
  params.set('page', String(Math.max(1, page)));
  if (folderId) params.set('folder_id', folderId);
  const res = await fetch(`/api/leistungen?${params.toString()}`, { signal });
  const data = res.ok ? await res.json().catch(() => ({})) : {};
  const payload = data as {
    items?: Leistung[];
    total_count?: number;
    total_pages?: number;
    page?: number;
  };
  const rows = Array.isArray(payload.items) ? payload.items : [];
  const total = Number(payload.total_count);
  const safeTotal = Number.isFinite(total) && total >= 0 ? total : rows.length;
  const totalPagesRaw = Number(payload.total_pages);
  const totalPages =
    Number.isFinite(totalPagesRaw) && totalPagesRaw >= 1
      ? Math.trunc(totalPagesRaw)
      : Math.max(1, Math.ceil(safeTotal / STAMMDATEN_LIST_PAGE_SIZE) || 1);
  const pageRaw = Number(payload.page);
  return {
    rows,
    total: safeTotal,
    page: Number.isFinite(pageRaw) && pageRaw >= 1 ? Math.trunc(pageRaw) : Math.max(1, page),
    totalPages,
    searchMode: null,
    globalTotal: folderId === null ? safeTotal : undefined,
  };
}

export type CreateLeistungPayload = {
  article_number?: string | null;
  name: string;
  description: string | null;
  unit: string | null;
  net_cost: number;
  surcharge_percent: number;
  folder_id?: string;
};

export async function createLeistung(payload: CreateLeistungPayload): Promise<void> {
  const res = await fetch('/api/leistungen', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Leistung konnte nicht angelegt werden.');
}

export type UpdateLeistungPayload = {
  article_number?: string | null;
  name: string;
  description: string | null;
  unit: string;
  net_cost: number;
  surcharge_percent: number;
  folder_id?: string | null;
};

export async function updateLeistung(id: string, payload: UpdateLeistungPayload): Promise<void> {
  const res = await fetch(`/api/leistungen/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Speichern fehlgeschlagen.');
}

export async function deleteLeistung(id: string): Promise<void> {
  const res = await fetch(`/api/leistungen/${id}`, { method: 'DELETE' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Löschen fehlgeschlagen.');
}
